/**
 * Per-stop arrival/boarding times from Directus route.stops (JSONB).
 *
 * Use the exact stop labels from Directus (English + Sinhala), e.g. UP route:
 *
 * [
 *   { "name": "Makumbura (මාකුඹුර)", "time": "6.15 p.m." },
 *   { "name": "Maththala (මත්තල)", "time": "8.30 p.m." },
 *   { "name": "22 Handiya (22 හන්දිය)", "time": "8.35 p.m." },
 *   { "name": "Thanamalwila (තණමල්විල)", "time": "8.45 p.m." },
 *   { "name": "Wellawaya (වැල්ලවාය)", "time": "9.30 p.m." },
 *   { "name": "Ella (ඇල්ල)", "time": "10.00 p.m." },
 *   { "name": "Kumbalwela (කුඹල්වෙල)", "time": "10.15 p.m." },
 *   { "name": "Haliela (හාලිඇල)", "time": "10.45 p.m." },
 *   { "name": "Badulla (බදුල්ල)", "time": "11.00 p.m." }
 * ]
 *
 * Booking pickup/drop (locations collection) should use the same name strings.
 * Matching compares the text before "(" (case/spacing insensitive).
 */

function parseStopsRaw(val) {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    const t = val.trim();
    if (!t) return [];
    try {
      return parseStopsRaw(JSON.parse(t));
    } catch {
      return t
        .split(/[,|]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  if (typeof val === "object" && Array.isArray(val.stops)) {
    return parseStopsRaw(val.stops);
  }
  return [];
}

function stopNameFromEntry(entry) {
  if (entry == null) return "";
  if (typeof entry === "string" || typeof entry === "number") {
    return String(entry).trim();
  }
  if (typeof entry === "object") {
    return String(
      entry.name ?? entry.label ?? entry.title ?? entry.stop ?? "",
    ).trim();
  }
  return String(entry).trim();
}

function stopTimeFromEntry(entry) {
  if (!entry || typeof entry !== "object") return "";
  const t =
    entry.time ?? entry.arrival_time ?? entry.arrivalTime ?? entry.reach_time;
  return t != null ? String(t).trim() : "";
}

/** @returns {{ name: string, time: string, label: string }[]} */
export function parseRouteStopSchedule(stopsRaw) {
  return parseStopsRaw(stopsRaw)
    .map((entry) => {
      const name = stopNameFromEntry(entry);
      const time = stopTimeFromEntry(entry);
      return {
        name,
        time,
        label: time ? `${name} — ${time}` : name,
      };
    })
    .filter((s) => s.name);
}

/** Match key from Directus / booking label — text before "(", letters+digits only. */
export function locationMatchKey(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return "";
  const beforeParen = raw.split("(")[0].trim();
  const normalized = beforeParen.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (normalized) return normalized;
  return beforeParen.toLowerCase().replace(/\s+/g, "");
}

function keysMatch(a, b) {
  const sa = String(a ?? "").trim();
  const sb = String(b ?? "").trim();
  if (sa && sb && sa === sb) return true;

  const ka = locationMatchKey(a);
  const kb = locationMatchKey(b);
  if (!ka || !kb) return false;
  if (ka === kb) return true;
  if (ka.startsWith(kb) || kb.startsWith(ka)) return true;
  return false;
}

/** Fuzzy index for route search (pickup/drop vs Directus stop list). */
export function findStopIndex(stopsRaw, location) {
  const entries = parseStopsRaw(stopsRaw);
  if (!entries.length) return -1;
  return entries.findIndex((entry) => keysMatch(location, stopNameFromEntry(entry)));
}

/** Pickup must appear before drop in Directus route.stops order. */
export function routeContainsPickupBeforeDrop(stopsRaw, pickUp, dropping) {
  const pi = findStopIndex(stopsRaw, pickUp);
  const di = findStopIndex(stopsRaw, dropping);
  return pi !== -1 && di !== -1 && pi < di;
}

function parseTimeToMinutes(timeStr) {
  const s = String(timeStr ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!s) return null;

  const dotted = s.match(/^(\d{1,2})[.:](\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i);
  if (dotted) {
    let hour = Number.parseInt(dotted[1], 10);
    const minute = Number.parseInt(dotted[2], 10);
    const meridiem = (dotted[3] || "").replace(/\./g, "").replace(/\s/g, "");
    if (meridiem.startsWith("p") && hour < 12) hour += 12;
    if (meridiem.startsWith("a") && hour === 12) hour = 0;
    if (!meridiem && hour <= 12 && /pm/i.test(s)) hour = hour === 12 ? 12 : hour + 12;
    return hour * 60 + minute;
  }

  const compact = s.match(/^(\d{1,2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)$/i);
  if (compact) {
    let hour = Number.parseInt(compact[1], 10);
    const meridiem = compact[2].replace(/\./g, "").replace(/\s/g, "");
    if (meridiem.startsWith("p") && hour < 12) hour += 12;
    if (meridiem.startsWith("a") && hour === 12) hour = 0;
    return hour * 60;
  }

  return null;
}

function formatMinutesToDisplay(totalMin) {
  const normalized = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours24 = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const isPm = hours24 >= 12;
  let hour12 = hours24 % 12;
  if (hour12 === 0) hour12 = 12;
  return `${String(hour12).padStart(2, "0")}.${String(mins).padStart(2, "0")} ${isPm ? "PM" : "AM"}`;
}

function fillInterpolatedTimes(schedule) {
  if (schedule.length <= 1) return schedule;

  const out = schedule.map((s) => ({ ...s }));
  const minutes = out.map((s) => parseTimeToMinutes(s.time));

  let i = 0;
  while (i < out.length) {
    if (minutes[i] != null) {
      i += 1;
      continue;
    }

    let prev = i - 1;
    while (prev >= 0 && minutes[prev] == null) prev -= 1;

    let next = i + 1;
    while (next < out.length && minutes[next] == null) next += 1;

    if (prev >= 0 && next < out.length && minutes[prev] != null && minutes[next] != null) {
      const span = next - prev;
      for (let j = prev + 1; j < next; j += 1) {
        const ratio = (j - prev) / span;
        minutes[j] = Math.round(minutes[prev] + ratio * (minutes[next] - minutes[prev]));
        out[j].time = formatMinutesToDisplay(minutes[j]);
      }
      i = next + 1;
    } else {
      i += 1;
    }
  }

  return out;
}

/**
 * @param {object} [options]
 * @param {string} [options.originLocation]
 * @param {string} [options.originTime]
 * @param {string} [options.destinationLocation]
 * @param {string} [options.destinationTime]
 */
export function buildEffectiveRouteSchedule(stopsRaw, options = {}) {
  const {
    originLocation = "",
    originTime = "",
    destinationLocation = "",
    destinationTime = "",
  } = options;

  const directus = parseRouteStopSchedule(stopsRaw);
  if (!directus.length) return [];

  let schedule = directus.map((s) => ({ name: s.name, time: s.time || "" }));

  if (originTime && originLocation) {
    const idx = schedule.findIndex((s) => keysMatch(s.name, originLocation));
    if (idx >= 0 && !schedule[idx].time) schedule[idx].time = originTime;
  }

  if (destinationTime && destinationLocation) {
    const idx = schedule.findIndex((s) => keysMatch(s.name, destinationLocation));
    if (idx >= 0 && !schedule[idx].time) schedule[idx].time = destinationTime;
    else if (schedule.length && !schedule[schedule.length - 1].time) {
      schedule[schedule.length - 1].time = destinationTime;
    }
  }

  return fillInterpolatedTimes(schedule);
}

/**
 * Reach-by time at pickup — uses Directus stop `time` when matched.
 * @param {object} [options] — originLocation, originTime, destinationLocation, destinationTime
 */
export function resolvePickupArrivalTime(
  stopsRaw,
  pickupLocation,
  fallbackTime = "",
  options = {},
) {
  const pickup = String(pickupLocation ?? "").trim();
  const schedule = buildEffectiveRouteSchedule(stopsRaw, {
    ...options,
    originTime: options.originTime ?? fallbackTime,
  });

  if (!pickup) {
    return String(fallbackTime ?? "").trim() || "Not specified";
  }

  for (const stop of schedule) {
    if (keysMatch(pickup, stop.name) && stop.time) {
      return stop.time;
    }
  }

  const firstStop = schedule[0];
  if (firstStop && keysMatch(pickup, firstStop.name)) {
    return (
      firstStop.time ||
      String(fallbackTime ?? "").trim() ||
      "Not specified"
    );
  }

  return "Not specified";
}

/** Display label for SMS/PDF — English part before "(" (keeps "22 Handiya"). */
export function locationDisplayLabel(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return "";
  const beforeParen = raw.split("(")[0].trim();
  return beforeParen || raw;
}
